import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Icon,
  Input,
  useDisclosure,
  useToast,
  Spacer,
} from "@chakra-ui/react";
import { Table, Space } from "antd";
import { RiSearch2Line } from "react-icons/ri";
import axios from "axios";
import HelpCenterDeleteConfirmation from "../modals/HelpCenterDeleteConfirmation";
import { get } from "lodash";

interface UserData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface BaseItem {
  _id: string;
  userId: string | UserData | null;
  description: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface BugItem extends BaseItem {
  status: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const HelpcenterMasterTable: React.FC<{ query: string }> = ({ query }) => {
  const [activeTab, setActiveTab] = useState<
    "bugReport" | "feedback" | "contactUs"
  >("bugReport");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState("");
  const [bugData, setBugData] = useState<BugItem[]>([]);
  const [feedbackData, setFeedbackData] = useState<BaseItem[]>([]);
  const [contactData, setContactData] = useState<BaseItem[]>([]);
  const toast = useToast();

  const searchColumns = {
    bugReport: ["username", "email", "description", "status"],
    feedback: ["username", "email", "description", "status"],
    contactUs: ["username", "email", "description", "status"],
  };
  const handleSearch = (data: any[]) => {
    if (!searchText) return data;

    return data.filter((item) => {
      return searchColumns[activeTab].some((column) => {
        const value = get(item, column, "");
        return value
          .toString()
          .toLowerCase()
          .includes(searchText.toLowerCase());
      });
    });
  };

  const formatTableData = (data: any[], type: string) => {
    // return data.map((item, index) => ({
    //   key: item._id,
    //   username: item.userId
    //     ? typeof item.userId === "object"
    //       ? `${item.userId.firstName} ${item.userId.lastName}`
    //       : "-"
    //     : "-",
    //   email:
    //     item.userId && typeof item.userId === "object"
    //       ? item.userId.email
    //       : "-",
    //   description: item.description,
    //   datereported: formatDate(item.createdAt),
    //   datesubmitted: formatDate(item.createdAt),
    //   status: item.status || "-",
    // }));
    const formattedData = data.map((item, index) => ({
      key: item._id,
      username: item.userId
        ? typeof item.userId === "object"
          ? `${item.userId.firstName} ${item.userId.lastName}`
          : "-"
        : "-",
      email:
        item.userId && typeof item.userId === "object"
          ? item.userId.email
          : "-",
      description: item.description,
      datereported: formatDate(item.createdAt),
      datesubmitted: formatDate(item.createdAt),
      status: item.status || "-",
    }));

    return handleSearch(formattedData);
  };

  const bugColumns = [
    {
      title: "Nos.",
      dataIndex: "key",
      width: 60,
      render: (_: any, __: any, index: number) => `#${index + 1}`,
    },
    {
      title: "Username",
      dataIndex: "username",
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 200,
    },
    {
      title: "Bug Description",
      dataIndex: "description",
      width: 300,
    },
    {
      title: "Date Reported",
      dataIndex: "datereported",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status: string) => (
        <span
          style={{
            padding: "6px 16px",
            borderRadius: "6px",
            backgroundColor: status === "resolved" ? "#d4edda" : "#f8d7da",
            color: status === "resolved" ? "#155724" : "#721c24",
            fontWeight: "400",
            fontSize: "14px",
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (record: any) => (
        <Space size="middle">
          <button
            style={{
              backgroundColor: "#02A432",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => handleResolveStatus(record)}
            disabled={record.status === "resolved"}
          >
            Resolve
          </button>
          <button
            style={{
              backgroundColor: "#D42F2F",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => deletes(record)}
          >
            Delete
          </button>
        </Space>
      ),
    },
  ];

  const feedbackColumns = [
    ...bugColumns.slice(0, 3),
    {
      title: "Feedback Description",
      dataIndex: "description",
      width: 300,
    },
    {
      title: "Date Submitted",
      dataIndex: "datesubmitted",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status: string) => (
        <span
          style={{
            padding: "6px 16px",
            borderRadius: "6px",
            backgroundColor: status === "addressed" ? "#d4edda" : "#f8d7da",
            color: status === "addressed" ? "#155724" : "#721c24",
            fontWeight: "400",
            fontSize: "14px",
          }}
        >
          {status}
        </span>
      ),
      // render: bugColumns[5].render,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (record: any) => (
        <Space size="middle">
          <button
            style={{
              backgroundColor: "#02A432",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => handleResolveStatus(record)}
            disabled={record.status === "addressed"}
          >
            Addressed
          </button>
          <button
            style={{
              backgroundColor: "#D42F2F",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => deletes(record)}
          >
            Delete
          </button>
        </Space>
      ),
    },
  ];

  const contactColumns = [
    ...bugColumns.slice(0, 3),
    {
      title: "Subject",
      dataIndex: "description",
      width: 300,
    },
    {
      title: "Date Submitted",
      dataIndex: "datesubmitted",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      // render: bugColumns[5].render,
      render: (status: string) => (
        <span
          style={{
            padding: "6px 16px",
            borderRadius: "6px",
            backgroundColor: status === "responded" ? "#d4edda" : "#f8d7da",
            color: status === "responded" ? "#155724" : "#721c24",
            fontWeight: "400",
            fontSize: "14px",
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (record: any) => (
        <Space size="middle">
          <button
            style={{
              backgroundColor: "#02A432",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => handleResolveStatus(record)}
            disabled={record.status === "responded"}
          >
            Addressed
          </button>
          <button
            style={{
              backgroundColor: "#D42F2F",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
            onClick={() => deletes(record)}
          >
            Delete
          </button>
        </Space>
      ),
    },
  ];

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  // const [targetData, setTargetData] = useState<any>();
  const [targetData, setTargetData] = useState<{
    record: BaseItem | BugItem;
    type: string;
  } | null>(null);
  // const deletes = (value: any) => {
  //   setTargetData(value);
  //   onDeleteOpen();
  // };
  const refreshData = async () => {
    try {
      if (activeTab === "bugReport") {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/bug`
        );
        setBugData(response.data.data);
      } else if (activeTab === "feedback") {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/feedback`
        );
        setFeedbackData(response.data.data);
      } else if (activeTab === "contactUs") {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/contact-us`
        );
        setContactData(response.data.data);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  // const handleDelete = (record: any) => {
  //   let originalData;
  //   if (activeTab === "bugReport") {
  //     originalData = bugData.find((item) => item._id === record.key);
  //   } else if (activeTab === "feedback") {
  //     originalData = feedbackData.find((item) => item._id === record.key);
  //   } else if (activeTab === "contactUs") {
  //     originalData = contactData.find((item) => item._id === record.key);
  //   }

  //   if (originalData) {
  //     setTargetData({
  //       record: originalData,
  //       type: activeTab,
  //     });
  //     onDeleteOpen();
  //   }
  // };
  const deletes = (record: any) => {
    let originalData;
    if (activeTab === "bugReport") {
      originalData = bugData.find((item) => item._id === record.key);
    } else if (activeTab === "feedback") {
      originalData = feedbackData.find((item) => item._id === record.key);
    } else if (activeTab === "contactUs") {
      originalData = contactData.find((item) => item._id === record.key);
    }

    if (originalData) {
      setTargetData({
        record: originalData,
        type: activeTab,
      });
      onDeleteOpen();
    }
  };
  const handleResolveStatus = async (record: any) => {
    try {
      let endpoint = "";
      let originalData;

      switch (activeTab) {
        case "bugReport":
          endpoint = "bug-report";
          originalData = bugData.find((item) => item._id === record.key);
          break;
        case "feedback":
          endpoint = "feedback";
          originalData = feedbackData.find((item) => item._id === record.key);
          break;
        case "contactUs":
          endpoint = "contact-us";
          originalData = contactData.find((item) => item._id === record.key);
          break;
      }

      if (originalData) {
        const response = await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/admin/${endpoint}/${originalData._id}`,
          {
            status: "resolved",
          }
        );

        if (response.data) {
          toast({
            title: `Status updated to resolved`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          refreshData();
        }
      }
    } catch (error) {
      toast({
        title: "Error updating status",
        description: "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error(error);
    }
  };
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (activeTab === "bugReport") {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/admin/bug`
          );
          setBugData(response.data.data);
        } else if (activeTab === "feedback") {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/admin/feedback`
          );
          setFeedbackData(response.data.data);
        } else if (activeTab === "contactUs") {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/admin/contact-us`
          );
          setContactData(response.data.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, [activeTab]);

  const currentData = useMemo(() => {
    const dataMap = {
      bugReport: formatTableData(bugData, "bug"),
      feedback: formatTableData(feedbackData, "feedback"),
      contactUs: formatTableData(contactData, "contact"),
    };
    return dataMap[activeTab];
  }, [activeTab, bugData, feedbackData, contactData, searchText]);

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        maxHeight: "86vh",
        height: "86vh",
        top: "8vh",
        left: "4vw",
        overflowY: "auto",
        position: "relative",
        backgroundColor: "#b2b2b233",
        padding: "20px 20px",
        borderRadius: "10px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* <Flex w="full" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            bg="#ffffff80"
            border="1px solid #b2b2b233"
            borderRadius="8px"
            w="600px"
            p={1}
          >
            <Box
              pl={2}
              display="flex"
              alignItems="center"
              color="#9E9E9E"
              fontSize="2xl"
            >
              <Icon as={RiSearch2Line} />
            </Box>
            <Box h="20px" w="1px" bg="#b2b2b2" mx={2} />
            <Input
              placeholder="Search or type a command"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                fontSize: "small",
                outline: "none",
                border: "none",
                background: "transparent",
              }}
            />
          </Box>
        </Flex> */}
        <Box
          display="flex"
          alignItems="center"
          bg="#FFF"
          border="1px solid #b2b2b233"
          borderRadius="12px"
          w="498px"
          padding="8px"
        >
          <Box
            pl={2}
            display="flex"
            alignItems="center"
            color="#9E9E9E"
            fontSize="2xl"
          >
            <img
              src="/icons/searchlogo.svg"
              alt="searchlogo"
              style={{ height: "24px", width: "24px" }}
            />
          </Box>

          <Input
            id="search"
            type="text"
            placeholder="Search or type a command"
            onChange={(e) => setSearchText(e.target.value)}
            fontSize="sm"
            border="none"
            outline="none"
            color="#757575"
            _focus={{
              border: "none",
              outline: "none",
              boxShadow: "none", // Add this line to remove the blue outline
            }}
            bg={"transparent"}
            style={{
              outline: "none",
              boxShadow: "none", // Add this line to ensure outline is removed in all browsers
            }}
          />
        </Box>

        <Spacer />

        <Flex gap="5">
          <Box
            background={activeTab === "bugReport" ? "#E24441" : "white"}
            fontSize="sm"
            color={activeTab === "bugReport" ? "#fff" : "black"}
            w="fit-content"
            textAlign="center"
            p={3}
            cursor="pointer"
            borderRadius="8px"
            onClick={() => setActiveTab("bugReport")}
          >
            Bug Report
          </Box>
          <Box
            background={activeTab === "feedback" ? "#E24441" : "white"}
            fontSize="sm"
            color={activeTab === "feedback" ? "#fff" : "black"}
            w="fit-content"
            textAlign="center"
            p={3}
            cursor="pointer"
            borderRadius="8px"
            onClick={() => setActiveTab("feedback")}
          >
            Feedback
          </Box>
          <Box
            background={activeTab === "contactUs" ? "#E24441" : "white"}
            fontSize="sm"
            color={activeTab === "contactUs" ? "#fff" : "black"}
            w="fit-content"
            textAlign="center"
            p={3}
            cursor="pointer"
            borderRadius="8px"
            onClick={() => setActiveTab("contactUs")}
          >
            Contact Us
          </Box>
        </Flex>
        <Table
          rowSelection={rowSelection}
          rowKey="key"
          columns={
            activeTab === "bugReport"
              ? bugColumns
              : activeTab === "feedback"
              ? feedbackColumns
              : contactColumns
          }
          dataSource={currentData}
          scroll={{ x: "max-content" }}
          size="middle"
          pagination={{
            position: ["bottomRight"],
            pageSize: 9,
          }}
          style={{
            backgroundColor: "white",
            borderRadius: 6,
          }}
          rowClassName={(record: any) => record.color}
        />
      </div>
      {/* {isDeleteOpen && targetData && (
        <HelpCenterDeleteConfirmation
          isOpen
          onClose={onDeleteClose}
          helpData={targetData}
          selectedTab={activeTab}
        />
      )} */}
      {isDeleteOpen && targetData && (
        <HelpCenterDeleteConfirmation
          isOpen={isDeleteOpen}
          onClose={() => {
            onDeleteClose();
            setTargetData(null);
          }}
          helpData={targetData.record}
          selectedTab={targetData.type}
          onDeleteSuccess={refreshData}
        />
      )}
    </div>
  );
};

export default HelpcenterMasterTable;
